"use client"

import React, { memo, useCallback, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Paper, PaperId } from "@/stores/slices/paperSlice";
import { useStore } from "@/stores/appStore";

type HeaderProps = {
  papers: Paper[];
  onToggle: (papers: Paper[], checked: boolean) => void;
};

export const SelectionHeader = memo(function SelectionHeader({ papers, onToggle }: HeaderProps) {
  const selectedIdsList = useStore(state => state.selectedIdsList);
  const checked = useMemo(() => {
    const selectedCount = papers.reduce((count, paper) => (
      selectedIdsList[paper.paperId] ? count + 1 : count
    ), 0);

    if (selectedCount === papers.length) return true;
    if (selectedCount === 0) return false;
    return "indeterminate";
  }, [papers, selectedIdsList]);

  // 稳定化 onCheckedChange 回调，避免每次渲染都创建新函数
  const handleCheckedChange = useCallback((value: boolean | string) => {
    onToggle(papers, !!value);
  }, [onToggle, papers]);

  return (
    <Checkbox
      checked={checked}
      onCheckedChange={handleCheckedChange}
      aria-label="全选"
      className={cn(
        "translate-y-[2px] data-[state=unchecked]:border-foreground",
        !papers.length && "invisible"
      )}
    />
  );
}, (prevProps, nextProps) => {
  // 自定义比较函数，只在关键 props 变化时重新渲染
  return (
    prevProps.papers.length === nextProps.papers.length &&
    prevProps.papers.every((paper, index) => paper.paperId === nextProps.papers[index]?.paperId) &&
    prevProps.onToggle === nextProps.onToggle
  );
});

type CellProps = {
  paperId: PaperId;
  onToggle: (paperId: PaperId, checked: boolean) => void;
};

export const SelectionCell = memo(function SelectionCell({ paperId, onToggle }: CellProps) {
  const checked = useStore(state => state.selectedIdsList[paperId] === true);

  // 稳定化 onCheckedChange 回调，避免每次渲染都创建新函数
  const handleCheckedChange = useCallback((value: boolean | string) => {
    onToggle(paperId, !!value);
  }, [onToggle, paperId]);

  return (
    <Checkbox
      checked={checked}
      onCheckedChange={handleCheckedChange}
      aria-label="选中行"
      className="translate-y-[2px] data-[state=unchecked]:border-foreground"
    />
  );
}, (prevProps, nextProps) => {
  // 自定义比较函数，只在关键 props 变化时重新渲染
  return (
    prevProps.paperId === nextProps.paperId &&
    prevProps.onToggle === nextProps.onToggle
  );
});
